import type {
  ApprovalLetterSummary, SBAReviewCycle, UnmatchedLabel, SBASummaryForDrug,
  UnmatchedLabelReason,
} from '../types';

export interface LabelOption {
  drugVersionId: number;
  versionNumber: number;
  effectiveDate: string | null;
  isCurrent: boolean;
  fdaSubmissionId: number | null;      // null = unmatched (no supplement)
  unmatchedReason: UnmatchedLabelReason | null;
}

export interface DocOption {
  id: number;
  label: string;
  date: string | null;
  fdaSubmissionId: number | null;      // null = unclassified
}

export interface DocGroup {
  key: string;                          // `${fdaSubmissionId}` or 'UNCLASSIFIED'
  fdaSubmissionId: number | null;
  headerDate: string | null;
  submissionLabel: string;
  submissionClass: string | null;
  docs: DocOption[];
}

export interface LetterOption {
  id: number;
  versionStr: string;
  date: string | null;
  fdaSubmissionId: number | null;
}

export interface LetterGroup {
  key: string;
  fdaSubmissionId: number | null;
  headerDate: string | null;
  submissionLabel: string;
  submissionClass: string | null;
  letters: LetterOption[];
}

export function submissionLabelOf(cycle: SBAReviewCycle): string {
  const type = (cycle.submission_type || '').toUpperCase();
  if (type === 'ORIG') return 'Original';
  if (type === 'SUPPL' && cycle.submission_number != null) return `Suppl ${cycle.submission_number}`;
  if (cycle.submission_number != null) return `${cycle.submission_type ?? ''} ${cycle.submission_number}`.trim();
  return cycle.submission_type || 'Submission';
}

// newest-first by effective_date then version_number desc; null dates sort last
function byDateDescThenVersion(a: LabelOption, b: LabelOption): number {
  const ad = a.effectiveDate ?? '', bd = b.effectiveDate ?? '';
  if (ad !== bd) return ad < bd ? 1 : -1;
  return b.versionNumber - a.versionNumber;
}

export function buildLabelOptions(cycles: SBAReviewCycle[], unmatched: UnmatchedLabel[]): LabelOption[] {
  const seen = new Set<number>();
  const out: LabelOption[] = [];
  for (const c of cycles) {
    const lvs = c.label_versions ?? [];
    if (lvs.length === 0) continue;
    for (const lv of lvs) {
      if (seen.has(lv.drug_version_id)) continue;
      seen.add(lv.drug_version_id);
      out.push({
        drugVersionId: lv.drug_version_id, versionNumber: lv.version_number,
        effectiveDate: lv.effective_date, isCurrent: lv.is_current,
        fdaSubmissionId: c.fda_submission_id,
        unmatchedReason: null,
      });
    }
  }
  for (const u of unmatched) {
    if (seen.has(u.drug_version_id)) continue;
    seen.add(u.drug_version_id);
    out.push({
      drugVersionId: u.drug_version_id, versionNumber: u.version_number,
      effectiveDate: u.effective_date, isCurrent: u.is_current,
      fdaSubmissionId: null, unmatchedReason: u.reason,
    });
  }
  return out.sort(byDateDescThenVersion);
}

export function attachedLabelId(cycle: SBAReviewCycle): number | null {
  const lvs = cycle.label_versions ?? [];
  if (lvs.length === 0) return null;
  return lvs.reduce((min, lv) => (lv.version_number < min.version_number ? lv : min)).drug_version_id;
}

function docOptionOf(d: SBASummaryForDrug): DocOption {
  return { id: d.id, label: d.doc_type_label, date: d.submission_status_date ?? null, fdaSubmissionId: d.fda_submission_id ?? null };
}

// One entry per review type. `?cycle=all` returns every size-split slice
// (`…_0…_5`) and every revision as its own row, all sharing a doc_type_label —
// showing 6 "Integrated Review" rows for one review is wrong. Keep the lowest
// id per label (the cover `_0` slice / canonical row), matching the per-cycle
// dedup the old submission card did.
function dedupeByLabel(docs: DocOption[]): DocOption[] {
  const best = new Map<string, DocOption>();
  for (const d of docs) {
    const prev = best.get(d.label);
    if (!prev || d.id < prev.id) best.set(d.label, d);
  }
  return [...best.values()];
}

export function buildDocGroups(documents: SBASummaryForDrug[], cycles: SBAReviewCycle[]): DocGroup[] {
  const cycleById = new Map<number, SBAReviewCycle>();
  cycles.forEach(c => cycleById.set(c.fda_submission_id, c));
  const byKey = new Map<string, DocOption[]>();
  for (const d of documents) {
    const sid = d.fda_submission_id ?? null;
    const key = sid == null ? 'UNCLASSIFIED' : String(sid);
    (byKey.get(key) ?? byKey.set(key, []).get(key)!).push(docOptionOf(d));
  }
  const groups: DocGroup[] = [];
  for (const [key, rawDocs] of byKey) {
    if (key === 'UNCLASSIFIED') continue;
    const c = cycleById.get(Number(key));
    const docs = dedupeByLabel(rawDocs).sort((a, b) => a.label.localeCompare(b.label));
    groups.push({
      key, fdaSubmissionId: Number(key),
      headerDate: c?.status_date ?? docs[0]?.date ?? null,
      submissionLabel: c ? submissionLabelOf(c) : 'Submission',
      submissionClass: c?.submission_class_code ?? null,
      docs,
    });
  }
  groups.sort((a, b) => {
    const ad = a.headerDate ?? '', bd = b.headerDate ?? '';
    if (ad !== bd) return ad < bd ? 1 : -1;
    return (b.fdaSubmissionId ?? 0) - (a.fdaSubmissionId ?? 0);
  });
  const unclassified = byKey.get('UNCLASSIFIED');
  if (unclassified && unclassified.length) {
    // NOT deduped: an unclassified doc has no trusted submission, so same-label
    // docs here are distinct docs, not size-split slices of one review. Keeping
    // them all also makes the visible rows match the envelope unclassified count.
    const docs = [...unclassified].sort((a, b) => a.label.localeCompare(b.label));
    groups.push({ key: 'UNCLASSIFIED', fdaSubmissionId: null, headerDate: null, submissionLabel: 'Unclassified', submissionClass: null, docs });
  }
  return groups;
}

/**
 * Map any RAW document id to the canonical dropdown entry's id — the lowest
 * id among docs sharing its (submission, doc_type_label), matching what
 * `dedupeByLabel` keeps. A `?source=sba` deep link can point at a size-split
 * slice / older revision that dedupe removed from the dropdown; the active
 * face must still resolve to the surviving entry instead of showing "No ...".
 * Returns null when the id isn't among `documents`.
 */
export function canonicalDocId(rawId: number, documents: SBASummaryForDrug[]): number | null {
  const raw = documents.find(d => d.id === rawId);
  if (!raw) return null;
  const rawSid = raw.fda_submission_id ?? null;
  // Unclassified rows are intentionally not deduped in buildDocGroups: two
  // same-label documents without a trusted submission remain distinct menu
  // entries. Preserve their exact ids instead of collapsing them together.
  if (rawSid == null) return raw.id;
  let canonical = raw.id;
  for (const d of documents) {
    if ((d.fda_submission_id ?? null) === rawSid && d.doc_type_label === raw.doc_type_label && d.id < canonical) {
      canonical = d.id;
    }
  }
  return canonical;
}

export function firstDocIdForSubmission(fdaSubmissionId: number, documents: SBASummaryForDrug[]): number | null {
  const matches = dedupeByLabel(
    documents.filter(d => d.fda_submission_id === fdaSubmissionId).map(docOptionOf),
  );
  if (matches.length === 0) return null;
  matches.sort((a, b) => a.label.localeCompare(b.label));
  return matches[0]!.id;
}

export function defaultLabelId(labels: LabelOption[]): number | null {
  if (labels.length === 0) return null;
  const current = labels.find(l => l.isCurrent);
  return (current ?? labels[0]!).drugVersionId; // labels already newest-first
}

export function docIdForLabel(label: LabelOption, documents: SBASummaryForDrug[]): number | null {
  if (label.fdaSubmissionId == null) return null;
  return firstDocIdForSubmission(label.fdaSubmissionId, documents);
}

export function attachedLabelIdForDoc(doc: DocOption, cycles: SBAReviewCycle[]): number | null {
  if (doc.fdaSubmissionId == null) return null;
  const c = cycles.find(c => c.fda_submission_id === doc.fdaSubmissionId);
  return c ? attachedLabelId(c) : null;
}

function letterOptionOf(letter: ApprovalLetterSummary): LetterOption {
  return {
    id: letter.id,
    versionStr: letter.version_str,
    date: letter.letter_date,
    fdaSubmissionId: letter.fda_submission_id,
  };
}

function byLetterDateThenId(a: LetterOption, b: LetterOption): number {
  const ad = a.date ?? '';
  const bd = b.date ?? '';
  if (ad !== bd) return ad < bd ? 1 : -1;
  return b.id - a.id;
}

export function letterOfCycle(
  cycle: SBAReviewCycle,
  letters: ApprovalLetterSummary[],
): LetterOption | null {
  const matches = letters
    .filter(letter => letter.fda_submission_id === cycle.fda_submission_id)
    .map(letterOptionOf)
    .sort(byLetterDateThenId);
  return matches[0] ?? null;
}

export function buildLetterGroups(
  letters: ApprovalLetterSummary[],
  cycles: SBAReviewCycle[],
): LetterGroup[] {
  const cycleById = new Map(cycles.map(cycle => [cycle.fda_submission_id, cycle]));
  const grouped = new Map<string, LetterOption[]>();
  for (const letter of letters) {
    const option = letterOptionOf(letter);
    const key = option.fdaSubmissionId == null
      ? 'UNCLASSIFIED'
      : String(option.fdaSubmissionId);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(option);
    else grouped.set(key, [option]);
  }

  const out: LetterGroup[] = [];
  for (const [key, bucket] of grouped) {
    if (key === 'UNCLASSIFIED') continue;
    const cycle = cycleById.get(Number(key));
    out.push({
      key,
      fdaSubmissionId: Number(key),
      headerDate: cycle?.status_date ?? bucket[0]?.date ?? null,
      submissionLabel: cycle ? submissionLabelOf(cycle) : 'Submission',
      submissionClass: cycle?.submission_class_code ?? null,
      letters: [...bucket].sort(byLetterDateThenId),
    });
  }
  out.sort((a, b) => {
    const ad = a.headerDate ?? '';
    const bd = b.headerDate ?? '';
    if (ad !== bd) return ad < bd ? 1 : -1;
    return (b.fdaSubmissionId ?? 0) - (a.fdaSubmissionId ?? 0);
  });

  const unclassified = grouped.get('UNCLASSIFIED');
  if (unclassified?.length) {
    out.push({
      key: 'UNCLASSIFIED',
      fdaSubmissionId: null,
      headerDate: null,
      submissionLabel: 'Unclassified',
      submissionClass: null,
      letters: [...unclassified].sort(byLetterDateThenId),
    });
  }
  return out;
}
